import React, { useEffect, useState } from 'react';
import { Modal } from '@/components';
import { useForm, useController } from 'react-hook-form';
import {
  BoxV2 as Box,
  Flex,
  Button,
  TextInput,
  DropdownV2 as Dropdown,
} from 'portal-commons';
import {
  isValidInput,
  createMaxLengthValidator,
  createRequiredValidator,
} from '@/utils/validator';
import { CampaignDetail, CampaignOperationItem } from '../../types';
import { SelectItem } from '@/types';
import { campaignController } from '@/apis/core';
import { toastFlashMessage } from '@/utils';

interface Props {
  open: boolean;
  campaign: CampaignDetail;
  categories: SelectItem[];
  carrier: CampaignOperationItem;
  isRedacted: boolean;
  onClose: (reload: boolean) => void;
}

interface FormValues {
  explanation: string;
  suspensionCategory: string;
}

const CarrierSuspendModal: React.FC<Props> = ({
  open,
  campaign,
  categories = [],
  carrier,
  isRedacted,
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const { control, reset, handleSubmit } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      explanation: '',
      suspensionCategory: '',
    },
  });

  const {
    field: { value: category, onChange: setCategory },
    fieldState: { error: categoryError },
  } = useController({
    control,
    name: 'suspensionCategory',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please select Suspension Category'
        ),
      },
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter explanation'),
        maxLength: createMaxLengthValidator(3000),
        pattern: (value) => isValidInput(value) || 'Invalid input',
      },
    },
  });

  const onValidHandler = async (data: FormValues) => {
    setIsProcessing(true);
    const result = await campaignController.updateCampaignOperationStatus(
      {
        campaignUid: campaign.uid,
        networkId: carrier.networkId,
        status: 'SUSPENDED',
      },
      data
    );
    if (result) {
      toastFlashMessage(
        `${carrier.rulesEngineResultsJson.mno} successfully suspended`,
        'success'
      );
      onClose(true);
    }

    setIsProcessing(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testid="carrierSuspendModal">
      <Box
        as="h3"
        sx={{
          py: 's',
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: 'normal',
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        Suspend Campaign
      </Box>
      <form noValidate onSubmit={handleSubmit(onValidHandler)}>
        <Flex sx={{ flexDirection: 'column', gap: 'm', mt: 15 }}>
          <Box
            as="span"
            sx={{
              fontSize: 'H300',
              fontWeight: 400,
              color: 't.black80',
              mb: '-1px',
            }}
          >
            * Indicates a Required Field
          </Box>
          <TextInput
            readOnly
            label="Campaign ID"
            value={campaign.uid ?? 'N/A'}
          />
          <Flex sx={{ gap: 's' }}>
            <TextInput
              readOnly
              label="Brand ID"
              value={campaign.brand.uid ?? 'N/A'}
              style={{ width: '124px' }}
            />
            <TextInput
              readOnly
              label="Brand DBA Name"
              value={
                isRedacted
                  ? 'Redacted due to PII'
                  : campaign.brand.displayName || 'N/A'
              }
              style={{ flexGrow: 1 }}
            />
          </Flex>
          <Flex sx={{ gap: 's' }}>
            <TextInput
              readOnly
              label="CSP ID"
              value={campaign.brand.csp.uid ?? 'N/A'}
              style={{ width: '124px' }}
            />
            <TextInput
              readOnly
              label="CSP DBA Name"
              value={campaign.brand.csp.displayName ?? 'N/A'}
              style={{ flexGrow: 1 }}
            />
          </Flex>
          <TextInput
            readOnly
            label="Carrier"
            value={carrier.rulesEngineResultsJson?.mno ?? '--'}
          />
          <Dropdown
            disabled={isProcessing}
            data-testid="suspensionCategory"
            label="Campaign Suspension Category*"
            options={categories}
            error={!!categoryError?.message}
            helperText={categoryError?.message}
            value={category}
            onChange={(value: string) => {
              setCategory(value);
            }}
          />
          <TextInput
            required
            multiline
            maxRows={4}
            data-testid="suspensionExplanation"
            label="Explanation"
            placeholder="Please enter Explanation"
            disabled={isProcessing}
            value={explanation}
            error={!!explanationError?.message}
            note={explanationError?.message}
            onChange={setExplanation}
            onBlur={validateExplanation}
          />
          <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
            <Button
              variant="outline"
              data-testid="suspendCampaignCancelButton"
              disabled={isProcessing}
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={isProcessing}
              data-testid="suspendCampaignSubmitButton"
              type="submit"
            >
              Suspend
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default CarrierSuspendModal;
